import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import './Settings.css';

interface Settings {
  adminEmail: string;
  siteName?: string;
  siteDescription?: string;
  contactPhone?: string;
  contactAddress?: string;
  commissionRate?: number;
  minDepositAmount?: number;
  waitingTimeMinutes?: number;
  confirmationWaitingTimeMinutes?: number;
  maintenanceMode?: boolean;
  featuredBannerUrl?: string;
  voucherBannerUrl?: string;
  startWorkingHour?: number;
  afterHoursGeneralLaborHour?: number;
  afterHoursAgriculturalLaborHour?: number;
  afterHoursHealthcareHour?: number;
}

const formatVnNumber = (value?: number) => {
  const amount = typeof value === 'number' && !isNaN(value) ? value : 0;
  return new Intl.NumberFormat('vi-VN').format(amount);
};

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({
    adminEmail: '',
    siteName: '',
    siteDescription: '',
    contactPhone: '',
    contactAddress: '',
    commissionRate: 0,
    minDepositAmount: 0,
    waitingTimeMinutes: 0,
    confirmationWaitingTimeMinutes: 0,
    maintenanceMode: false,
    featuredBannerUrl: '',
    voucherBannerUrl: '',
    startWorkingHour: 5,
    afterHoursGeneralLaborHour: 21,
    afterHoursAgriculturalLaborHour: 18,
    afterHoursHealthcareHour: 20
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState({
    featuredBanner: false,
    voucherBanner: false
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải cài đặt...');
      
      const settingsDoc = doc(db, 'settings', 'app');
      const settingsSnapshot = await getDoc(settingsDoc);

      if (settingsSnapshot.exists()) {
        const data = settingsSnapshot.data();
        console.log('Dữ liệu cài đặt:', data);
        
        setSettings({
          adminEmail: data.adminEmail || '',
          siteName: data.siteName || 'FConnect Admin',
          siteDescription: data.siteDescription || 'Hệ thống quản lý dịch vụ',
          contactPhone: data.contactPhone || '',
          contactAddress: data.contactAddress || '',
          commissionRate: data.commissionRate || 10,
          minDepositAmount: typeof data.minDepositAmount === 'number' ? data.minDepositAmount : 0,
          waitingTimeMinutes: typeof data.waitingTimeMinutes === 'number' ? data.waitingTimeMinutes : 0,
          confirmationWaitingTimeMinutes: typeof data.confirmationWaitingTimeMinutes === 'number' ? data.confirmationWaitingTimeMinutes : 0,
          maintenanceMode: data.maintenanceMode || false,
          featuredBannerUrl: data.featuredBannerUrl || '',
          voucherBannerUrl: data.voucherBannerUrl || '',
          startWorkingHour: typeof data.startWorkingHour === 'number' ? data.startWorkingHour : 5,
          afterHoursGeneralLaborHour: typeof data.afterHoursGeneralLaborHour === 'number' ? data.afterHoursGeneralLaborHour : 21,
          afterHoursAgriculturalLaborHour: typeof data.afterHoursAgriculturalLaborHour === 'number' ? data.afterHoursAgriculturalLaborHour : 18,
          afterHoursHealthcareHour: typeof data.afterHoursHealthcareHour === 'number' ? data.afterHoursHealthcareHour : 20
        });
      } else {
        console.log('Không tìm thấy tài liệu cài đặt, sử dụng giá trị mặc định...');
        // Sử dụng cài đặt mặc định nếu chưa có
        setSettings({
          adminEmail: '',
          siteName: 'FConnect Admin',
          siteDescription: 'Hệ thống quản lý dịch vụ',
          contactPhone: '',
          contactAddress: '',
          commissionRate: 10,
          minDepositAmount: 0,
          waitingTimeMinutes: 0,
          confirmationWaitingTimeMinutes: 0,
          maintenanceMode: false,
          featuredBannerUrl: '',
          voucherBannerUrl: '',
          startWorkingHour: 5,
          afterHoursGeneralLaborHour: 21,
          afterHoursAgriculturalLaborHour: 18,
          afterHoursHealthcareHour: 20
        });
      }
      
    } catch (error: any) {
      console.error('Lỗi khi tải cài đặt:', error);
      
      let errorMessage = 'Không thể tải cài đặt';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập cài đặt. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      console.log('Bắt đầu lưu cài đặt...', settings);
      
      const settingsDoc = doc(db, 'settings', 'app');
      await setDoc(settingsDoc, {
        adminEmail: settings.adminEmail,
        siteName: settings.siteName,
        siteDescription: settings.siteDescription,
        contactPhone: settings.contactPhone,
        contactAddress: settings.contactAddress,
        commissionRate: settings.commissionRate,
        minDepositAmount: settings.minDepositAmount ?? 0,
        waitingTimeMinutes: settings.waitingTimeMinutes ?? 0,
        confirmationWaitingTimeMinutes: settings.confirmationWaitingTimeMinutes ?? 0,
        maintenanceMode: settings.maintenanceMode,
        featuredBannerUrl: settings.featuredBannerUrl,
        voucherBannerUrl: settings.voucherBannerUrl,
        startWorkingHour: settings.startWorkingHour ?? 5,
        afterHoursGeneralLaborHour: settings.afterHoursGeneralLaborHour ?? 21,
        afterHoursAgriculturalLaborHour: settings.afterHoursAgriculturalLaborHour ?? 18,
        afterHoursHealthcareHour: settings.afterHoursHealthcareHour ?? 20,
        updatedAt: new Date()
      }, { merge: true }); // merge: true sẽ cập nhật các trường có sẵn và thêm trường mới

      console.log('Đã lưu cài đặt thành công');
      setSuccess('Cài đặt đã được lưu thành công!');
      
    } catch (error: any) {
      console.error('Lỗi khi lưu cài đặt:', error);
      
      let errorMessage = 'Không thể lưu cài đặt';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền cập nhật cài đặt. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof Settings, value: string | number | boolean) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const uploadImage = async (file: File, bannerType: 'featuredBanner' | 'voucherBanner') => {
    try {
      setUploading(prev => ({ ...prev, [bannerType]: true }));
      setError(null);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Vui lòng chọn file hình ảnh hợp lệ');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Kích thước file không được vượt quá 5MB');
      }

      const fileName = `banners/${bannerType}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      console.log(`Đang tải lên ${bannerType}...`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log(`${bannerType} đã tải lên thành công:`, downloadURL);
      
      // Update settings with new URL
      const fieldName = bannerType === 'featuredBanner' ? 'featuredBannerUrl' : 'voucherBannerUrl';
      handleInputChange(fieldName, downloadURL);
      
      setSuccess(`${bannerType === 'featuredBanner' ? 'Featured Banner' : 'Voucher Banner'} đã tải lên thành công!`);
      
    } catch (error: any) {
      console.error(`Lỗi khi tải lên ${bannerType}:`, error);
      
      let errorMessage = `Không thể tải lên ${bannerType}`;
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền tải lên hình ảnh. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(prev => ({ ...prev, [bannerType]: false }));
    }
  };

  const deleteBanner = async (bannerType: 'featuredBanner' | 'voucherBanner') => {
    try {
      const fieldName = bannerType === 'featuredBanner' ? 'featuredBannerUrl' : 'voucherBannerUrl';
      const currentUrl = settings[fieldName];
      
      if (!currentUrl) {
        return;
      }

      // Delete from storage if it's a Firebase storage URL
      if (currentUrl.includes('firebase')) {
        try {
          const storageRef = ref(storage, currentUrl);
          await deleteObject(storageRef);
          console.log(`Đã xóa ${bannerType} khỏi storage`);
        } catch (error) {
          console.log(`Không thể xóa file khỏi storage:`, error);
          // Continue anyway, just clear the URL
        }
      }
      
      // Clear the URL from settings
      handleInputChange(fieldName, '');
      setSuccess(`${bannerType === 'featuredBanner' ? 'Featured Banner' : 'Voucher Banner'} đã được xóa!`);
      
    } catch (error: any) {
      console.error(`Lỗi khi xóa ${bannerType}:`, error);
      setError(`Không thể xóa ${bannerType}: ${error.message}`);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, bannerType: 'featuredBanner' | 'voucherBanner') => {
    const file = event.target.files?.[0];
    if (file) {
      uploadImage(file, bannerType);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div className="settings">
        <div className="page-header">
          <h2>Cài đặt hệ thống</h2>
          <p className="page-subtitle">Quản lý cài đặt chung của hệ thống</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải cài đặt...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="page-header">
        <h2>Cài đặt hệ thống</h2>
        <p className="page-subtitle">Quản lý cài đặt chung của hệ thống</p>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchSettings();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <div>
            <strong>{success}</strong>
          </div>
          <button 
            onClick={() => setSuccess(null)}
            className="close-button"
          >
            ✕
          </button>
        </div>
      )}

      <div className="settings-container">
        <div className="settings-section">
          <h3>Thông tin liên hệ</h3>
          
          <div className="form-group">
            <label htmlFor="adminEmail">Email quản trị viên *</label>
            <input
              id="adminEmail"
              type="email"
              value={settings.adminEmail}
              onChange={(e) => handleInputChange('adminEmail', e.target.value)}
              placeholder="admin@example.com"
              required
            />
            <small>Email chính để nhận thông báo và liên hệ</small>
          </div>

          <div className="form-group">
            <label htmlFor="contactPhone">Số điện thoại liên hệ</label>
            <input
              id="contactPhone"
              type="tel"
              value={settings.contactPhone}
              onChange={(e) => handleInputChange('contactPhone', e.target.value)}
              placeholder="+84 123 456 789"
            />
            <small>Số điện thoại hỗ trợ khách hàng</small>
          </div>

          <div className="form-group">
            <label htmlFor="contactAddress">Địa chỉ</label>
            <textarea
              id="contactAddress"
              value={settings.contactAddress}
              onChange={(e) => handleInputChange('contactAddress', e.target.value)}
              placeholder="Nhập địa chỉ công ty..."
              rows={3}
            />
            <small>Địa chỉ văn phòng hoặc trụ sở chính</small>
          </div>
        </div>

        <div className="settings-section">
          <h3>Thông tin website</h3>
          
          <div className="form-group">
            <label htmlFor="siteName">Tên website</label>
            <input
              id="siteName"
              type="text"
              value={settings.siteName}
              onChange={(e) => handleInputChange('siteName', e.target.value)}
              placeholder="FConnect Admin"
            />
            <small>Tên hiển thị của website</small>
          </div>

          <div className="form-group">
            <label htmlFor="siteDescription">Mô tả website</label>
            <textarea
              id="siteDescription"
              value={settings.siteDescription}
              onChange={(e) => handleInputChange('siteDescription', e.target.value)}
              placeholder="Mô tả ngắn về website..."
              rows={3}
            />
            <small>Mô tả ngắn gọn về dịch vụ</small>
          </div>
        </div>

        <div className="settings-section">
          <h3>Cài đặt kinh doanh</h3>
          
          <div className="form-group">
            <label htmlFor="commissionRate">Tỷ lệ hoa hồng mặc định (%)</label>
            <input
              id="commissionRate"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.commissionRate}
              onChange={(e) => handleInputChange('commissionRate', parseFloat(e.target.value) || 0)}
              placeholder="10"
            />
            <small>Tỷ lệ hoa hồng mặc định cho các dịch vụ mới</small>
          </div>

          <div className="form-group">
            <label htmlFor="minDepositAmount">Số tiền ký quỹ tối thiểu (VND)</label>
            <input
              id="minDepositAmount"
              type="number"
              min="0"
              step="1000"
              value={settings.minDepositAmount ?? 0}
              onChange={(e) => handleInputChange('minDepositAmount', parseFloat(e.target.value) || 0)}
              placeholder="0"
            />
            <small>Số tiền ký quỹ tối thiểu yêu cầu để kích hoạt tài khoản — Hiển thị: {formatVnNumber(settings.minDepositAmount)} VND</small>
          </div>

          <div className="form-group">
            <label htmlFor="waitingTimeMinutes">Thời gian chờ (phút)</label>
            <input
              id="waitingTimeMinutes"
              type="number"
              min="0"
              step="1"
              value={settings.waitingTimeMinutes ?? 0}
              onChange={(e) => handleInputChange('waitingTimeMinutes', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
            <small>Thời gian chờ mặc định áp dụng trong quy trình đặt dịch vụ (tính bằng phút)</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmationWaitingTimeMinutes">Thời gian chờ xác nhận (phút)</label>
            <input
              id="confirmationWaitingTimeMinutes"
              type="number"
              min="0"
              step="1"
              value={settings.confirmationWaitingTimeMinutes ?? 0}
              onChange={(e) => handleInputChange('confirmationWaitingTimeMinutes', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
            <small>Khoảng thời gian tối đa chờ xác nhận trước khi hết hạn (tính bằng phút)</small>
          </div>

          <div className="form-group">
            <label htmlFor="startWorkingHour">Giờ bắt đầu làm việc</label>
            <input
              id="startWorkingHour"
              type="number"
              min="0"
              max="23"
              step="1"
              value={settings.startWorkingHour ?? 5}
              onChange={(e) => handleInputChange('startWorkingHour', Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
              placeholder="5"
            />
            <small>Giờ bắt đầu ngày làm việc tiêu chuẩn (0-23). Mặc định: 5 (5 AM)</small>
          </div>

          <div className="form-group">
            <label htmlFor="afterHoursGeneralLaborHour">Giờ ngoài giờ lao động phổ thông</label>
            <input
              id="afterHoursGeneralLaborHour"
              type="number"
              min="0"
              max="23"
              step="1"
              value={settings.afterHoursGeneralLaborHour ?? 21}
              onChange={(e) => handleInputChange('afterHoursGeneralLaborHour', Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
              placeholder="21"
            />
            <small>Giờ bắt đầu tính ngoài giờ cho Lao động phổ thông (0-23). Mặc định: 21 (9 PM)</small>
          </div>

          <div className="form-group">
            <label htmlFor="afterHoursAgriculturalLaborHour">Giờ ngoài giờ lao động nông nghiệp</label>
            <input
              id="afterHoursAgriculturalLaborHour"
              type="number"
              min="0"
              max="23"
              step="1"
              value={settings.afterHoursAgriculturalLaborHour ?? 18}
              onChange={(e) => handleInputChange('afterHoursAgriculturalLaborHour', Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
              placeholder="18"
            />
            <small>Giờ bắt đầu tính ngoài giờ cho Lao động nông nghiệp (0-23). Mặc định: 18 (6 PM)</small>
          </div>

          <div className="form-group">
            <label htmlFor="afterHoursHealthcareHour">Giờ ngoài giờ dịch vụ y tế</label>
            <input
              id="afterHoursHealthcareHour"
              type="number"
              min="0"
              max="23"
              step="1"
              value={settings.afterHoursHealthcareHour ?? 20}
              onChange={(e) => handleInputChange('afterHoursHealthcareHour', Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
              placeholder="20"
            />
            <small>Giờ bắt đầu tính ngoài giờ cho Dịch vụ y tế (0-23). Mặc định: 20 (8 PM)</small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.maintenanceMode}
                onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
              />
              <span className="checkmark"></span>
              Chế độ bảo trì
            </label>
            <small>Khi bật, website sẽ hiển thị thông báo bảo trì</small>
          </div>
        </div>

        <div className="settings-section">
          <h3>Quản lý Banner</h3>
          
          {/* Featured Banner */}
          <div className="form-group banner-group">
            <label>Featured Banner</label>
            {settings.featuredBannerUrl && (
              <div className="banner-preview">
                <img 
                  src={settings.featuredBannerUrl} 
                  alt="Featured Banner" 
                  className="banner-image"
                />
                <button 
                  onClick={() => deleteBanner('featuredBanner')}
                  className="delete-banner-btn"
                  type="button"
                >
                  Xóa hình
                </button>
              </div>
            )}
            <div className="banner-upload">
              <input
                type="file"
                id="featuredBannerUpload"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'featuredBanner')}
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="featuredBannerUpload" 
                className={`upload-btn ${uploading.featuredBanner ? 'uploading' : ''}`}
              >
                {uploading.featuredBanner ? 'Đang tải lên...' : (settings.featuredBannerUrl ? 'Thay đổi hình' : 'Tải lên hình')}
              </label>
            </div>
            <small>Kích thước khuyến nghị: 1200x400px, tối đa 5MB</small>
          </div>

          {/* Voucher Banner */}
          <div className="form-group banner-group">
            <label>Voucher Banner</label>
            {settings.voucherBannerUrl && (
              <div className="banner-preview">
                <img 
                  src={settings.voucherBannerUrl} 
                  alt="Voucher Banner" 
                  className="banner-image"
                />
                <button 
                  onClick={() => deleteBanner('voucherBanner')}
                  className="delete-banner-btn"
                  type="button"
                >
                  Xóa hình
                </button>
              </div>
            )}
            <div className="banner-upload">
              <input
                type="file"
                id="voucherBannerUpload"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'voucherBanner')}
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="voucherBannerUpload" 
                className={`upload-btn ${uploading.voucherBanner ? 'uploading' : ''}`}
              >
                {uploading.voucherBanner ? 'Đang tải lên...' : (settings.voucherBannerUrl ? 'Thay đổi hình' : 'Tải lên hình')}
              </label>
            </div>
            <small>Kích thước khuyến nghị: 1200x300px, tối đa 5MB</small>
          </div>
        </div>

        <div className="settings-actions">
          <button 
            onClick={saveSettings} 
            disabled={saving}
            className="save-button"
          >
            {saving ? 'Đang lưu...' : 'Lưu cài đặt'}
          </button>
          
          <button 
            onClick={fetchSettings}
            disabled={saving}
            className="reset-button"
          >
            Làm mới
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings; 